import { Field, FieldOption, FieldType } from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { MfValidationsValidator } from '@/bpc/utils/MfValidationsValidator';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import { getValueByPath } from '@mjolnir/mf-fields-ts/src/types/utils';

export class OnboardingField {
  private readonly mfField: Field;
  readonly payload: Payload;
  private applicableCache?: boolean;
  private customConfigurationCache?: Record<string, unknown>;
  private mandatoryCache?: boolean;
  private filledCache?: boolean;
  private disabledCache?: boolean;
  readonly absolutePath: string;
  readonly type: FieldType;
  readonly label: string;
  readonly tooltipText?: string;
  readonly fallbackTooltip?: string;
  readonly fallbackLabel: string;
  readonly trackFieldValueWithAnalytics: boolean;
  readonly validator?: MfValidationsValidator;
  readonly enumLimits?: { min?: number; max?: number };
  readonly applicableDependsOn: string[];

  constructor(
    absolutePath: string,
    field: Field,
    payload: Payload,
    enumLimits?: { min?: number; max?: number },
  ) {
    this.payload = payload;
    this.mfField = field;
    this.trackFieldValueWithAnalytics = field.trackFieldValueWithAnalytics;
    this.absolutePath = absolutePath;
    this.type = field.type;
    this.label = field.label;
    this.tooltipText = field.tooltip;
    this.fallbackTooltip = field.fallbackTooltip;
    this.fallbackLabel = field.fallbackLabel;
    this.applicableDependsOn = field.applicableDependsOn || [];
    if (field.mfValidation != null) {
      this.validator = new MfValidationsValidator(
        field.mfValidation,
        () => this.payload,
        this.absolutePath,
      );
    }
    this.enumLimits = enumLimits;
  }

  public applicable(): boolean {
    if (this.applicableCache === undefined) {
      this.applicableCache = this.mfField.applicable(this.payload, this.absolutePath);
    }
    return this.applicableCache;
  }

  public customConfiguration(): Record<string, unknown> | undefined {
    if (this.customConfigurationCache === undefined) {
      this.customConfigurationCache = this.mfField.customConfiguration?.(this.payload);
    }
    return this.customConfigurationCache;
  }

  public mandatory(): boolean {
    if (this.mandatoryCache === undefined) {
      this.mandatoryCache = this.mfField.mandatory(this.payload, this.absolutePath);
    }
    return this.mandatoryCache;
  }

  public disabled(): boolean {
    if (this.disabledCache === undefined) {
      this.disabledCache = this.mfField.disabled(this.payload, this.absolutePath);
    }

    return this.disabledCache;
  }

  public clearCaches(): void {
    this.applicableCache = undefined;
    this.customConfigurationCache = undefined;
    this.mandatoryCache = undefined;
    this.filledCache = undefined;
    this.disabledCache = undefined;
  }

  public enumOptions(): FieldOption[] | undefined {
    return this.mfField.options;
  }

  public filled(): boolean {
    if (this.filledCache === undefined) {
      const value = getValueByPath(this.payload, this.absolutePath);

      if (Array.isArray(value)) {
        this.filledCache = value.length > 0;
      } else {
        this.filledCache = value !== null && value !== undefined && value !== '';
      }
    }
    return this.filledCache;
  }

  public labelForEnumValue(value: string): string {
    return this.mfField.options?.find(option => option.key === value)?.label as string;
  }

  public fallbackLabelForEnumValue(value: string): string {
    return this.mfField.options?.find(option => option.key === value)?.fallbackLabel as string;
  }
}
